import { CATEGORIES, isCarsTrucks } from '@kijiji/category'
import { type IconProps } from '@kijiji/icons/src/components/Icon'
import AccessibilityIcon from '@kijiji/icons/src/icons/Accessibility'
import AppliancesIcon from '@kijiji/icons/src/icons/Appliances'
import AutosModelIcon from '@kijiji/icons/src/icons/AutosModel'
import AutosSeatsSimpleIcon from '@kijiji/icons/src/icons/AutosSeatsSimple'
import AutosTrailerHitchIcon from '@kijiji/icons/src/icons/AutosTrailerHitch'
import ColorSimpleIcon from '@kijiji/icons/src/icons/ColorSimple'
import ConditionCertifiedPreOwnedIcon from '@kijiji/icons/src/icons/ConditionCertifiedPreOwned'
import EngineIcon from '@kijiji/icons/src/icons/Engine'
import FileIcon from '@kijiji/icons/src/icons/File'
import FuelIcon from '@kijiji/icons/src/icons/Fuel'
import FurnishedIcon from '@kijiji/icons/src/icons/Furnished'
import KmSimpleIcon from '@kijiji/icons/src/icons/KmSimple'
import SmokingIcon from '@kijiji/icons/src/icons/Smoking'
import TransmissionIcon from '@kijiji/icons/src/icons/Transmission'
import UtilitiesIcon from '@kijiji/icons/src/icons/Utilities'
import WiFiAndMoreIcon from '@kijiji/icons/src/icons/WiFiAndMore'
import { type FC } from 'react'

import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { type AttributeConfig } from '@/features/attributes/constants/attributesConfig'
import { ATTRIBUTES_LABEL_OVERRIDE } from '@/features/attributes/constants/attributesLabelOverride'

/**
 * Some categories will have attributes grouped for better visibility
 * Groups are statically defined in the FE, therefore they will need a label and icon static specification
 */

/** The AttributeGroups type will be defined per category (i.e. Real Estate) */
type AttributeGroups<T extends string> = {
  /**
   * When creating a new group name, that should match a translation in the listing's file: "attribute.group_label.<group_name>"
   * */
  [groupName in T]: {
    /** The id is an identifier that only needs to be added to the group when we will need to find it to modify it before displaying it */
    id?: string
    /**
     * List of attributes under the group
     * We can override any ATTRIBUTE_CONFIG definitions including the labels if we need an attribute to have a different label when it appears grouped
     */
    attributes: AttributeConfig
    /**
     * Some groups have too many attributes to be shown
     * this param indicates the attributes should be hidden until the user clicks to display them
     */
    hideAttributesOnRender?: boolean
    /** Icon assigned to the group */
    Icon?: FC<IconProps>
    /** It will have a valueIcon defined when the icon depends on the value of one of the attributes defined inside */
    valueIcon?: {
      canonicalName: string
      Icon: { [value: string]: FC<IconProps> }
    }
  }
}

type SupportedRealEstateGroups =
  | 'rental_agreement'
  | 'utilities'
  | 'furnished'
  | 'appliances'
  | 'includes'
  | 'connectivity'
  | 'smoking'
  | 'accessibility'

type SupportedVehiclesGroups =
  | 'certified_pre_owned'
  | 'condition'
  | 'kilometers'
  | 'transmission'
  | 'engine'
  | 'fuel'
  | 'seats'
  | 'body_style'
  | 'color'
  | 'towing'
  | 'model'

const ATTRIBUTE_GROUPS_REAL_ESTATE: AttributeGroups<SupportedRealEstateGroups> = {
  rental_agreement: {
    Icon: FileIcon,
    attributes: {
      [ATTRIBUTES.AGREEMENT_TYPE]: {
        labelOverride: ({ values }, t) =>
          t('listing:attribute.label_override.rental_agreement', { value: values[0] }),
      },
      [ATTRIBUTES.DATE_AVAILABLE]: {
        labelOverride: (attribute, t) => {
          const formattedDate = ATTRIBUTES_LABEL_OVERRIDE.FORMAT_DATE(attribute, t)
          return t('listing:attribute.label_override.date_available', { value: formattedDate })
        },
      },
      [ATTRIBUTES.TERM_AGREEMENT]: {
        labelOverride: ({ values }, t) =>
          t('listing:attribute.label_override.yes', { value: values[0] }),
      },
    },
  },
  utilities: {
    Icon: UtilitiesIcon,
    attributes: {
      [ATTRIBUTES.HEAT]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.INCLUDED },
      [ATTRIBUTES.HYDRO]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.INCLUDED },
      [ATTRIBUTES.WATER]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.INCLUDED },
    },
  },
  furnished: {
    Icon: FurnishedIcon,
    attributes: { [ATTRIBUTES.FURNISHED]: {} },
  },
  appliances: {
    Icon: AppliancesIcon,
    attributes: {
      [ATTRIBUTES.LAUNDRY_UNIT]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.LAUNDRY_BUILDING]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.DISHWASHER]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.FRIDGE]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
    },
  },
  includes: {
    attributes: {
      [ATTRIBUTES.BALCONY]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.YARD]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.AIR_CONDITIONING]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
    },
  },
  connectivity: {
    Icon: WiFiAndMoreIcon,
    attributes: {
      [ATTRIBUTES.CABLE_TV]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.INTERNET]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
    },
  },
  smoking: {
    Icon: SmokingIcon,
    attributes: {
      [ATTRIBUTES.SMOKING_PERMITTED]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
    },
  },
  accessibility: {
    Icon: AccessibilityIcon,
    hideAttributesOnRender: true,
    attributes: {
      [ATTRIBUTES.WHEELCHAIR_ACCESSIBLE]: {
        labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME,
      },
      [ATTRIBUTES.BRAILLE_LABELS]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.AUDIO_PROMPTS]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.BARRIER_FREE]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.VISUAL_AIDS]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME },
      [ATTRIBUTES.ACCESSIBLE_WASHROOMS]: {
        labelOverride: ATTRIBUTES_LABEL_OVERRIDE.BOOLEAN_NAME,
      },
    },
  },
}

const ATTRIBUTE_GROUPS_VEHICLES: AttributeGroups<SupportedVehiclesGroups> = {
  certified_pre_owned: {
    id: 'certified-pre-owned',
    Icon: ConditionCertifiedPreOwnedIcon,
    attributes: { [ATTRIBUTES.CPO]: {} },
  },
  condition: {
    id: 'condition',
    valueIcon: { canonicalName: ATTRIBUTES.VEHICLE_TYPE, Icon: {} },
    attributes: { [ATTRIBUTES.VEHICLE_TYPE]: {} },
  },
  kilometers: {
    Icon: KmSimpleIcon,
    attributes: {
      [ATTRIBUTES.CAR_MILEAGE]: { labelOverride: ATTRIBUTES_LABEL_OVERRIDE.FORMAT_NUMBER },
    },
  },
  transmission: { Icon: TransmissionIcon, attributes: { [ATTRIBUTES.CAR_TRANSMISSION]: {} } },
  engine: {
    Icon: EngineIcon,
    attributes: {
      [ATTRIBUTES.CYLINDERS]: {},
      [ATTRIBUTES.CAR_ENGINE_POWER]: {},
      [ATTRIBUTES.DRIVE_TRAIN]: {},
    },
  },
  fuel: {
    Icon: FuelIcon,
    valueIcon: { canonicalName: ATTRIBUTES.CAR_FUEL_TYPE, Icon: {} },
    attributes: {
      [ATTRIBUTES.CAR_FUEL_TYPE]: {},
      [ATTRIBUTES.ELECTRIC_RANGE]: {},
      [ATTRIBUTES.TIME_TO_CHARGE]: {},
    },
  },
  seats: {
    Icon: AutosSeatsSimpleIcon,
    attributes: {
      [ATTRIBUTES.NUM_SEATS]: {
        labelOverride: ({ values }, t) => {
          const value = values[0]
          if (!value || value === '0') return ''
          return t('attribute.label_override.seats', { value })
        },
      },
    },
  },
  body_style: {
    valueIcon: { canonicalName: ATTRIBUTES.CAR_BODY_TYPE, Icon: {} },
    attributes: {
      [ATTRIBUTES.CAR_BODY_TYPE]: {},
      [ATTRIBUTES.CAB_TYPE]: {},
      [ATTRIBUTES.NUM_DOORS]: {
        labelOverride: ({ values }, t) => {
          const value = values[0]
          if (!value || value === '0') return ''
          return t('attribute.label_override.doors', { value })
        },
      },
    },
  },
  color: { Icon: ColorSimpleIcon, attributes: { [ATTRIBUTES.CAR_COLOR]: {} } },
  towing: { Icon: AutosTrailerHitchIcon, attributes: { [ATTRIBUTES.TRAILERS_HITCH]: {} } },
  model: {
    Icon: AutosModelIcon,
    attributes: {
      [ATTRIBUTES.CAR_YEAR]: {},
      [ATTRIBUTES.CAR_MAKE]: {},
      [ATTRIBUTES.CAR_MODEL]: {},
      [ATTRIBUTES.CAR_TRIM]: {},
    },
  },
}

export const getAttrGroupsByCategoryId = (
  categoryId: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): AttributeGroups<any> | undefined => {
  /** Long term rentals */
  if (categoryId === CATEGORIES.APARTMENTS_CONDOS_CATEGORY_ID) {
    return ATTRIBUTE_GROUPS_REAL_ESTATE
  }

  if (isCarsTrucks(categoryId)) {
    return ATTRIBUTE_GROUPS_VEHICLES
  }
}
